import React, { useEffect, useRef } from 'react';
import { Helmet } from "react-helmet-async";

const VideoGallery = () => {
    // 1. Create mutable references for the DOM elements
    const viewportRef = useRef(null);
    const leftStripRef = useRef(null);
    const rightStripRef = useRef(null);

    useEffect(() => {
        // 2. Fetch the current underlying elements safely from the refs
        const viewport = viewportRef.current;
        const leftStrip = leftStripRef.current;
        const rightStrip = rightStripRef.current;

        // Safety Guard Clause: If elements aren't mounted yet, stop early.
        if (!viewport || !leftStrip || !rightStrip) return;

        let currentPosLeft = 0;
        let currentPosRight = 0;
        let isHovered = false;
        let animationFrameId;

        const speedLeft = 0.6;
        const speedRight = 0.4;

        // Hover handlers
        const handleMouseEnter = () => { isHovered = true; };
        const handleMouseLeave = () => { isHovered = false; };

        viewport.addEventListener('mouseenter', handleMouseEnter);
        viewport.addEventListener('mouseleave', handleMouseLeave);

        // Animation Loop Logic
        function performAutoScroll() {
            // Check existence inside the loop dynamically to prevent async teardown crashes
            if (!isHovered && leftStrip && rightStrip) {
                currentPosLeft -= speedLeft;
                currentPosRight -= speedRight;

                if (Math.abs(currentPosLeft) >= (leftStrip.scrollHeight / 2)) {
                    currentPosLeft = 0;
                }
                if (Math.abs(currentPosRight) >= (rightStrip.scrollHeight / 2)) {
                    currentPosRight = 0;
                }

                if (window.innerWidth > 991) {
                    leftStrip.style.transform = `translateY(${currentPosLeft}px)`;
                    rightStrip.style.transform = `translateY(${currentPosRight}px)`;
                } else {
                    leftStrip.style.transform = 'none';
                    rightStrip.style.transform = 'none';
                }
            }
            animationFrameId = requestAnimationFrame(performAutoScroll);
        }

        // Initialize the animation frame loop
        animationFrameId = requestAnimationFrame(performAutoScroll);

        // 3. CLEANUP: Vital step to clear loops and events when components change or unmount
        return () => {
            cancelAnimationFrame(animationFrameId);
            if (viewport) {
                viewport.removeEventListener('mouseenter', handleMouseEnter);
                viewport.removeEventListener('mouseleave', handleMouseLeave);
            }
        };
    }, []); // Empty array ensures this setup loop executes exactly once on component load

    return (
        <div>

            <Helmet>
                <title>Video Gallery | Cinematic Photography & Videography Portfolio | KhultStudios</title>

                <meta
                    name="description"
                    content="Explore the KhultStudios Video Gallery featuring cinematic wedding films, pre-wedding videos, corporate films, commercial ads, fashion films, product videos, event highlights, drone cinematography, and creative visual storytelling in Hyderabad."
                />

                <meta
                    name="keywords"
                    content="Video Gallery Hyderabad, Videography Portfolio Hyderabad, Wedding Films Hyderabad, Cinematic Wedding Videos, Pre Wedding Video Hyderabad, Corporate Video Production, Commercial Video Production, Product Videos Hyderabad, Fashion Films Hyderabad, Event Videography Hyderabad, Drone Videography Hyderabad, Promotional Videos, Brand Films, Creative Videography, KhultStudios"
                />

                <meta name="robots" content="index, follow" />
                <meta name="author" content="KhultStudios" />

                <link
                    rel="canonical"
                    href="https://khultstudios.com/video-gallery"
                />

                {/* Open Graph */}
                <meta property="og:type" content="website" />
                <meta
                    property="og:url"
                    content="https://khultstudios.com/video-gallery"
                />
                <meta
                    property="og:title"
                    content="Video Gallery | Cinematic Videography Portfolio | KhultStudios"
                />
                <meta
                    property="og:description"
                    content="Watch our portfolio of cinematic wedding films, commercial videos, corporate films, fashion videos, product videos, drone cinematography, and creative storytelling."
                />
                <meta
                    property="og:image"
                    content="https://khultstudios.com/images/video-gallery-banner.jpg"
                />
                <meta property="og:site_name" content="KhultStudios" />

                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta
                    name="twitter:title"
                    content="Video Gallery | KhultStudios"
                />
                <meta
                    name="twitter:description"
                    content="Explore cinematic wedding films, commercial videos, corporate productions, fashion films, product videos, and creative videography."
                />
                <meta
                    name="twitter:image"
                    content="https://khultstudios.com/images/video-gallery-banner.jpg"
                />

                {/* Favicon */}
                <link rel="icon" href="https://khultstudios.com/favicon.ico" />
            </Helmet>
            {/* FULLSCREEN VIDEO VIEWPORT BACKGROUND ENGINE */}
            <div className="video-bg-viewport">
                <video
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="global-html5-video"
                >
                    <source src="videos/khultstudio.mp4" type="video/mp4" />
                    Every picture tells a story worth remembering
                </video>
                <div className="vignette-overlay-layer" />
            </div>

            {/* INNER APP CANVAS CONTAINER SECTION */}
            <div className="page-frame px-2 px-md-5 pb-5">
                <div className="landing-container position-relative overflow-hidden">
                    <div className="paper-texture" />
                    <div className="container-fluid h-100 p-4 p-lg-5 position-relative z-2">
                        <div className="row h-100 gy-5">

                            {/* Content Brief side description */}
                            <div className="col-lg-5 d-flex flex-column justify-content-center h-100 content-side">
                                <div className="invitation-text position-relative py-4">
                                    <h5 className="text-uppercase tracking-wider text-orange fw-bold mb-2">
                                        KhultStudios Video Gallery
                                    </h5>
                                    <h1 className="display-3 text-uppercase main-title text-white mb-4">
                                        Every picture tells a story worth remembering
                                    </h1>
                                    <p className="lead-text text-light-gray mb-4 col-xl-10">
                                        KhultStudios is a creative photography and visual production studio dedicated to capturing life's most meaningful moments and transforming ideas into timeless works of art. With a passion for storytelling and an eye for detail, we create stunning images and cinematic visuals that reflect emotions, personality, and excellence.
                                    </p>
                                    <p className="lead-text text-light-gray mb-4 col-xl-10">
                                        From intimate pre-wedding and wedding photography to fashion, modelling, acting portfolios, commercial advertising, industrial shoots, product photography, celebrity and movie posters, child photography, creative concepts, and special events, our experienced team delivers exceptional quality tailored to every client's vision.
                                    </p>
                                    <p className="lead-text text-light-gray mb-4 col-xl-10">
                                        At KhultStudios, we believe every photograph should tell a unique story. Using the latest equipment, innovative techniques, and artistic creativity, we ensure every frame captures genuine emotions, beautiful moments, and unforgettable memories. Whether you're celebrating a milestone, building your personal brand, or promoting your business, we are committed to creating visuals that leave a lasting impression.
                                    </p>
                                    <a
                                        href="contact-us"
                                        className="btn btn-orange text-uppercase px-4 py-2 text-dark fw-bold tracking-wide btn-scroll"
                                    >
                                        Contact Us
                                    </a>
                                </div>
                                <div className="social-footer d-flex gap-3 mt-2">
                                    <a href="#" className="social-icon"><i className="fab fa-facebook-f" /></a>
                                    <a href="#" className="social-icon"><i className="fab fa-instagram" /></a>
                                    <a href="#" className="social-icon"><i className="fab fa-tiktok" /></a>
                                </div>
                            </div>

                            {/* Infinite Slanted Ribbon Gallery Strip components */}
                            <div className="col-lg-7 position-relative photo-zone-height">
                                <div className="filmstrip-skew-box">
                                    {/* Linked via ref hooks */}
                                    <div
                                        className="vertical-scroll-viewport"
                                        ref={viewportRef}
                                    >
                                        <div className="row gx-4 h-100">
                                            <div className="col-6">
                                                <div className="filmstrip-ribbon" ref={leftStripRef} style={{ display: 'none' }}>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1515934751635-c81c6bc9a2d8?q=80&w=500" alt="Wedding 1" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?q=80&w=500" alt="Wedding 2" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1519741497674-611481863552?q=80&w=500" alt="Wedding 3" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1511285560929-80b456fea0bc?q=80&w=500" alt="Wedding 4" />
                                                    </div>
                                                    {/* Clone Buffers */}
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1515934751635-c81c6bc9a2d8?q=80&w=500" alt="Wedding 1 Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1465495976277-4387d4b0b4c6?q=80&w=500" alt="Wedding 2 Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1519741497674-611481863552?q=80&w=500" alt="Wedding 3 Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1511285560929-80b456fea0bc?q=80&w=500" alt="Wedding 4 Buffer" />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-6">
                                                <div className="filmstrip-ribbon ribbon-offset" ref={rightStripRef}>
                                                    <div className="ribbon-cell">
                                                        <img src="/video-gallery/video-gallery.jpg" alt="Wedding 5" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/video-gallery/video-gallery1.jpg" alt="Wedding 6" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/video-gallery/video-gallery2.jpg" alt="Wedding 7" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/video-gallery/video-gallery3.jpg" alt="Wedding 8" />
                                                    </div>
                                                    {/* Clone Buffers */}
                                                    <div className="ribbon-cell">
                                                        <img src="/video-gallery/video-gallery.jpg" alt="Wedding 5 Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/video-gallery/video-gallery1.jpg" alt="Wedding 6 Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/video-gallery/video-gallery2.jpg" alt="Wedding 7 Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/video-gallery/video-gallery3.jpg" alt="Wedding 8 Buffer" />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </div>


        </div>
    );
};

export default VideoGallery;




